import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Slider,
  Stack,
  Paper,
  IconButton,
  LinearProgress,
  alpha,
  useTheme,
  useMediaQuery
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import {
  JOURNEY_PHASES,
  JOURNEY_STEPS,
  buildSerpentinePathD,
  getPhaseForStep,
  getStepByNumber,
  getStepLayoutPosition
} from './careerJourney100Data';

const PATH_D = buildSerpentinePathD();
const STEP_DWELL_MS = 10_000;
const MOVE_MS = 600;

function StepDetailSidebar({ stepNumber, playing, dwellMs }) {
  const current = getStepByNumber(stepNumber);
  const phase = getPhaseForStep(stepNumber);
  const [remaining, setRemaining] = useState(dwellMs);

  useEffect(() => {
    setRemaining(dwellMs);
    if (!playing) return undefined;
    const started = Date.now();
    const tick = window.setInterval(() => {
      const left = Math.max(0, dwellMs - (Date.now() - started));
      setRemaining(left);
    }, 200);
    return () => window.clearInterval(tick);
  }, [stepNumber, playing, dwellMs]);

  const progress = playing ? ((dwellMs - remaining) / dwellMs) * 100 : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        minHeight: { xs: 220, md: 420 },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: alpha('#818cf8', 0.35),
        background: phase ? phase.gradient : alpha('#6366f1', 0.25),
        boxShadow: `0 12px 40px ${alpha('#000', 0.25)}`
      }}
    >
      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="overline" sx={{ color: alpha('#fff', 0.75), fontWeight: 700, letterSpacing: 0.08 }}>
          Standing here
        </Typography>
        <Typography variant="h2" sx={{ fontWeight: 900, color: '#fff', lineHeight: 1, my: 0.5 }}>
          {current.step}
        </Typography>
        <Typography variant="caption" sx={{ color: alpha('#fff', 0.8), fontWeight: 600, mb: 1.5 }}>
          of 100 · {Math.round((current.step / 100) * 100)}% of journey
        </Typography>

        <Chip
          size="small"
          label={`Phase ${phase?.id}: ${phase?.name}`}
          sx={{
            alignSelf: 'flex-start',
            mb: 1.5,
            bgcolor: alpha('#000', 0.22),
            color: '#fff',
            fontWeight: 600,
            maxWidth: '100%',
            height: 'auto',
            '& .MuiChip-label': { whiteSpace: 'normal', py: 0.75 }
          }}
        />

        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#fff', mb: 1, lineHeight: 1.35 }}>
          {current.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: alpha('#fff', 0.92),
            lineHeight: 1.7,
            flex: 1,
            overflowY: 'auto',
            pr: 0.5
          }}
        >
          {current.description}
        </Typography>

        {playing && (
          <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${alpha('#fff', 0.2)}` }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.85), fontWeight: 600 }}>
                Next step in
              </Typography>
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 800 }}>
                {Math.ceil(remaining / 1000)}s
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: alpha('#000', 0.2),
                '& .MuiLinearProgress-bar': { bgcolor: '#fff' }
              }}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
}

/** Walker rendered in SVG user space (same units as dots) so it stays centered on the active step. */
function SvgWalker({ x, y, color, moving }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle
        r="2.75"
        fill={color}
        stroke="#fff"
        strokeWidth="0.2"
        filter="url(#careerWalkerGlow)"
      />
      {moving && (
        <circle r="2.75" fill="none" stroke={color} strokeWidth="0.15" opacity="0.7">
          <animate attributeName="r" values="2.75;3.35;2.75" dur="1.2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.7;0.2;0.7" dur="1.2s" repeatCount="indefinite" />
        </circle>
      )}
      <text
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="2.4"
        y="0.05"
        pointerEvents="none"
      >
        🚶
      </text>
    </g>
  );
}

export default function CareerJourneyPath100() {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const scrollRef = useRef(null);
  const [activeStep, setActiveStep] = useState(1);
  const [playing, setPlaying] = useState(false);
  const [hoverStep, setHoverStep] = useState(null);
  const [moving, setMoving] = useState(false);

  const current = getStepByNumber(activeStep);
  const phase = getPhaseForStep(activeStep);
  const pos = getStepLayoutPosition(activeStep - 1);

  const phaseSegments = useMemo(
    () =>
      JOURNEY_PHASES.map((p) => ({
        ...p,
        startIdx: p.stepRange[0] - 1,
        endIdx: p.stepRange[1] - 1
      })),
    []
  );

  const goToStep = useCallback((n) => {
    setActiveStep((prev) => {
      const next = Math.min(100, Math.max(1, n));
      if (next !== prev) {
        setMoving(true);
        window.setTimeout(() => setMoving(false), MOVE_MS);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!playing) return undefined;
    const id = window.setTimeout(() => {
      goToStep(activeStep >= 100 ? 1 : activeStep + 1);
    }, STEP_DWELL_MS);
    return () => window.clearTimeout(id);
  }, [playing, activeStep, goToStep]);

  const jumpPhase = (phaseId) => {
    const p = JOURNEY_PHASES.find((x) => x.id === phaseId);
    if (p) goToStep(p.stepRange[0]);
  };

  return (
    <Box sx={{ mt: { xs: 4, md: 6 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          background: alpha('#0f172a', 0.55),
          border: '1px solid',
          borderColor: alpha('#818cf8', 0.25),
          backdropFilter: 'blur(16px)',
          boxShadow: `0 24px 64px ${alpha('#000', 0.35)}`
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography variant="overline" sx={{ color: '#a5b4fc', fontWeight: 700, letterSpacing: 0.1 }}>
              Top 100 sequence · fully interactive
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#f1f5f9' }}>
              Walk the career progression line
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#e2e8f0', 0.6) }}>
              Play pauses 10 seconds on each step — details appear in the panel on the right.
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
            <IconButton
              size="small"
              onClick={() => goToStep(activeStep - 1)}
              disabled={activeStep <= 1}
              sx={{ color: '#e2e8f0' }}
              aria-label="Previous step"
            >
              <SkipPreviousIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setPlaying((p) => !p)}
              sx={{
                bgcolor: alpha('#6366f1', 0.35),
                color: '#fff',
                '&:hover': { bgcolor: alpha('#6366f1', 0.55) }
              }}
              aria-label={playing ? 'Pause walk' : 'Play walk'}
            >
              {playing ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => goToStep(activeStep + 1)}
              disabled={activeStep >= 100}
              sx={{ color: '#e2e8f0' }}
              aria-label="Next step"
            >
              <SkipNextIcon />
            </IconButton>
          </Stack>
        </Stack>

        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mb: 2.5 }}>
          {JOURNEY_PHASES.map((p) => (
            <Chip
              key={p.id}
              label={`${p.id}. ${compact ? p.shortName : p.name}`}
              size="small"
              onClick={() => jumpPhase(p.id)}
              sx={{
                fontWeight: phase?.id === p.id ? 700 : 500,
                bgcolor: phase?.id === p.id ? alpha(p.color, 0.35) : alpha(p.color, 0.12),
                color: '#f1f5f9',
                border: '1px solid',
                borderColor: phase?.id === p.id ? p.color : alpha(p.color, 0.35),
                '&:hover': { bgcolor: alpha(p.color, 0.28) }
              }}
            />
          ))}
        </Stack>

        {/* Path board + right detail sidebar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'stretch'
          }}
        >
          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              minWidth: 0,
              overflowX: 'auto',
              overflowY: 'hidden',
              pb: 1,
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#818cf8', 0.4), borderRadius: 3 }
            }}
          >
            <Box
              sx={{
                position: 'relative',
                minWidth: compact ? 680 : '100%',
                width: compact ? 680 : '100%',
                aspectRatio: '10 / 9',
                maxHeight: compact ? 480 : 440
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 100 90"
                preserveAspectRatio="xMidYMid meet"
                sx={{ width: '100%', height: '100%', display: 'block', overflow: 'visible' }}
              >
                <defs>
                  <linearGradient id="careerPathGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    {phaseSegments.map((p, i) => (
                      <stop key={p.id} offset={`${(i / phaseSegments.length) * 100}%`} stopColor={p.color} />
                    ))}
                    <stop offset="100%" stopColor={JOURNEY_PHASES[9].color} />
                  </linearGradient>
                  <filter id="careerGlow">
                    <feGaussianBlur stdDeviation="0.6" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                  <filter id="careerWalkerGlow">
                    <feDropShadow dx="0" dy="0.15" stdDeviation="0.35" floodColor="#000" floodOpacity="0.45" />
                  </filter>
                </defs>

                {JOURNEY_PHASES.map((p, row) => {
                  const y = 5 + (row / 9) * 82;
                  return (
                    <text
                      key={p.id}
                      x={2}
                      y={y + 0.5}
                      fill={alpha(p.color, 0.85)}
                      fontSize="1.8"
                      fontWeight="700"
                      fontFamily="inherit"
                    >
                      P{p.id}
                    </text>
                  );
                })}

                <path
                  d={PATH_D}
                  fill="none"
                  stroke={alpha('#64748b', 0.35)}
                  strokeWidth="0.55"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d={PATH_D}
                  fill="none"
                  stroke="url(#careerPathGrad)"
                  strokeWidth="0.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="100 100"
                  pathLength="100"
                  style={{
                    strokeDashoffset: 100 - ((activeStep - 1) / 99) * 100,
                    transition: `stroke-dashoffset ${MOVE_MS}ms ease`,
                    filter: 'url(#careerGlow)'
                  }}
                />

                {JOURNEY_STEPS.map((s, idx) => {
                  const { x, y } = getStepLayoutPosition(idx);
                  const p = getPhaseForStep(s.step);
                  const isActive = s.step === activeStep;
                  const isPast = s.step < activeStep;
                  const isHover = hoverStep === s.step;
                  const r = isActive ? 1.35 : isHover ? 1.15 : 0.85;
                  const showNumber = !isActive && (isHover || s.step % 10 === 0);
                  return (
                    <g
                      key={s.step}
                      style={{ cursor: 'pointer' }}
                      onClick={() => goToStep(s.step)}
                      onMouseEnter={() => setHoverStep(s.step)}
                      onMouseLeave={() => setHoverStep(null)}
                    >
                      <circle
                        cx={x}
                        cy={y}
                        r={r + 0.35}
                        fill={alpha(p.color, isPast || isActive ? 0.35 : 0.12)}
                        stroke={isActive ? '#fff' : alpha(p.color, 0.6)}
                        strokeWidth={isActive ? 0.2 : 0.12}
                      />
                      <circle
                        cx={x}
                        cy={y}
                        r={r}
                        fill={isActive ? p.color : isPast ? alpha(p.color, 0.85) : alpha('#1e293b', 0.9)}
                        stroke={alpha('#fff', isActive ? 0.9 : 0.25)}
                        strokeWidth="0.08"
                      />
                      {showNumber && (
                        <text
                          x={x}
                          y={y + 0.35}
                          textAnchor="middle"
                          fill="#fff"
                          fontSize="1.1"
                          fontWeight="700"
                          fontFamily="inherit"
                          pointerEvents="none"
                        >
                          {s.step}
                        </text>
                      )}
                    </g>
                  );
                })}

                <SvgWalker x={pos.x} y={pos.y} color={phase?.color || '#6366f1'} moving={moving || playing} />
              </Box>
            </Box>
          </Box>

          <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
            <StepDetailSidebar stepNumber={activeStep} playing={playing} dwellMs={STEP_DWELL_MS} />
          </Box>
        </Box>

        <Box sx={{ mt: 2.5, px: 0.5 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: alpha('#e2e8f0', 0.7) }}>
              {current.step}. {current.title}
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#e2e8f0', 0.7) }}>
              Step {activeStep} / 100
            </Typography>
          </Stack>
          <Slider
            value={activeStep}
            min={1}
            max={100}
            onChange={(_, v) => goToStep(v)}
            aria-label="Career journey step"
            sx={{
              color: phase?.color || '#818cf8',
              '& .MuiSlider-thumb': {
                width: 18,
                height: 18,
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }
            }}
          />
        </Box>

        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', color: alpha('#e2e8f0', 0.5), mt: 1.5 }}
        >
          Click a dot, drag the slider, or press play — the walker pauses 10 seconds on each milestone.
        </Typography>
      </Paper>
    </Box>
  );
}
